/**
 * Phase 6.0 — Product Detail Drawer (Sub-Phase 1b live migration).
 *
 * Full-screen overlay drawer that shows: hero image, name + strain pill,
 * description, vibe line, effect chips, size/variant selector with prices,
 * and optional Cannabinoids + Terpenes lab-data panels (only rendered when
 * the product carries `lab_data`).
 *
 * Backward compatible:
 *   - Products without `variants` or flower category fall back to a simple
 *     qty + add-to-cart row.
 *   - Products without `lab_data.cannabinoids` / `lab_data.terpenes` simply
 *     hide those panels.
 *
 * Triggered from `CustomerDashboard.js` when the user taps a product image
 * (replaces the older VibeView modal).
 */
import { useEffect, useRef, useState } from 'react';
import { ShoppingCart, ChevronDown, Info, Leaf, X, Heart, Plus, Minus } from 'lucide-react';
import { logEvent } from '../utils/eventsLogger';
import ProductRatingBadge from './reviews/ProductRatingBadge';
// Feb 2026 — Review UI wired behind the `customer.reviews` feature flag.
// Modal + list live in ReviewComponents.js; useFlag reads the runtime flag
// value the server seeds + broadcasts over the flag.updated SSE event.
import { ReviewSubmitModal, ProductReviewsSection } from './reviews/ReviewComponents';
import { ReviewWindowButton } from './reviews/ReviewWindowButton';
import { useFlag } from '../context/FeatureFlagsContext';
import {
  FLOWER_SIZES,
  DEFAULT_FLOWER_SIZE,
  flowerSizesForProduct,
  effectsForProduct,
  effectEmoji,
} from '../utils/flowerSizes';
import {
  hasVariants,
  getVariants,
  variantPriceFor,
  variantStockFor,
} from '../utils/productVariants';
import { cannabinoidFact, terpeneFact } from '../utils/labFacts';
import { customerEffectiveStock, isSoldOut } from '../utils/customerStock';
import { SoldOutButton } from './SoldOutButton';

export function ProductDetailDrawer({
  product,
  onClose,
  onAddToCart,
  region,
  isFavorite,
  onToggleFavorite,
}) {
  const isFlower = product?.category === 'flower';
  const isVariantMode = !isFlower && hasVariants(product);
  const flowerSizes = isFlower ? flowerSizesForProduct(product, region) : [];
  const variants = isVariantMode ? getVariants(product) : [];

  // Default selection: first in-stock variant; else first variant.
  const firstAvailableVariant = isVariantMode
    ? (variants.find((v) => variantStockFor(product, v.key, region) > 0) || variants[0])
    : null;
  const [selectedSize, setSelectedSize] = useState(
    isFlower
      ? DEFAULT_FLOWER_SIZE.key
      : (firstAvailableVariant ? firstAvailableVariant.key : null),
  );
  const [quantity, setQuantity] = useState(1);
  // Phase 6.0 — Tap-to-learn expansion state for cannabinoid + terpene chips.
  // Stores the key currently expanded for each panel. Tap a chip → toggles
  // its description below the grid. Only one expanded at a time per panel.
  const [expandedCb, setExpandedCb] = useState(null);
  const [expandedTp, setExpandedTp] = useState(null);
  // Feb 2026 — Review submit modal open/close state. Only reachable when
  // the `customer.reviews` flag is ON, so this state has no effect while
  // the launch flag is dark. Explicit `false` default so the reviews UI
  // doesn't flash between context load and the seeded flag value landing.
  const [showReviewModal, setShowReviewModal] = useState(false);
  const reviewsEnabled = useFlag('customer.reviews', false);

  // Sticky CTA only appears once size selector has scrolled out of view.
  const variantsBlockRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const el = variantsBlockRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback: always show on browsers without IntersectionObserver, OR
      // when the product has no variants (no selector block to observe).
      setShowStickyCta(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product?.id]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Feb 2026 — fire `product.viewed` when the drawer opens. This is the
  // canonical product-detail surface (Phase 6.0 Sub-Phase 1b replaced the
  // old VibeView modal). Dedup-keyed by product id so rapid open/close on
  // the same product doesn't spam the event stream.
  useEffect(() => {
    if (!product?.id) return;
    logEvent('product.viewed', {
      product_id: product.id,
      name: product.name,
      category: product.category,
      strain_type: product.strain_type,
      price: product.price,
      region: region || null,
    }, { dedupeKey: `pv_${product.id}` });
  }, [product?.id, product?.name, product?.category, product?.strain_type, product?.price, region]);

  if (!product) return null;

  const cb = product.lab_data?.cannabinoids;
  const tp = product.lab_data?.terpenes;
  const effects = isFlower ? effectsForProduct(product) : [];
  // Phase 6.0 Sub-Phase 2 — use region-aware stock so products with
  // per-region inventory (legacy `stock`=0, region_stock[region]>0) don't
  // wrongly render as OUT OF STOCK.
  const isOutOfStock = isSoldOut(product, region);

  // Resolve current price + add-to-cart payload.
  const flowerCfg = isFlower ? (flowerSizes.find((s) => s.key === selectedSize) || flowerSizes[0]) : null;
  const variantPrice = isVariantMode && selectedSize
    ? variantPriceFor(product, selectedSize, region)
    : null;
  const variantStock = isVariantMode && selectedSize
    ? variantStockFor(product, selectedSize, region)
    : null;
  const displayPrice = isFlower
    ? (flowerCfg?.price ?? product.price)
    : (isVariantMode ? variantPrice : product.price);

  // Find lowest-tier price for "from $X" line. Guards against empty arrays
  // (e.g. a future product config that disables all variants).
  const fromPrice = (() => {
    if (isFlower && flowerSizes.length > 0) {
      return Math.min(...flowerSizes.map((s) => s.price));
    }
    if (isVariantMode && variants.length > 0) {
      const prices = variants
        .map((v) => variantPriceFor(product, v.key, region))
        .filter((p) => typeof p === 'number' && p > 0);
      if (prices.length > 0) return Math.min(...prices);
    }
    return product.price;
  })();

  const handleAddToCart = () => {
    if (isFlower && flowerCfg) {
      onAddToCart(product, quantity, {
        size: flowerCfg.key,
        eighths: flowerCfg.eighths,
        unitPrice: flowerCfg.price,
      });
    } else if (isVariantMode && selectedSize) {
      onAddToCart(product, quantity, {
        size: selectedSize,
        eighths: 1,
        unitPrice: variantPrice,
      });
    } else {
      onAddToCart(product, quantity);
    }
    onClose();
  };

  const addDisabled = isOutOfStock
    || (isVariantMode && (!selectedSize || variantStock <= 0));

  return (
    <div
      className="fixed inset-0 z-50 bg-[#050003]/95 backdrop-blur-sm overflow-y-auto"
      data-testid="product-detail-drawer"
      role="dialog"
      aria-modal="true"
    >
      {/* Phase 6.0 hotfix — pad top with safe-area-inset so the CLOSE button
          doesn't sit under iOS Dynamic Island / status bar. */}
      <div
        className="max-w-2xl mx-auto px-4 sm:px-6 pb-32 space-y-5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <span className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">
            {'>'} PRODUCT_DETAIL
          </span>
          <button
            onClick={onClose}
            className="border border-[#FF3B30] text-[#FF3B30] px-3 py-1 font-mono text-xs uppercase rounded-none hover:bg-[rgba(255,59,48,0.1)] transition-colors flex items-center gap-1"
            data-testid="product-detail-close"
            aria-label="Close product detail"
          >
            <X className="w-3 h-3" /> CLOSE
          </button>
        </div>

        {/* Photo + header */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full aspect-video object-cover rounded-sm mb-3"
            />
          ) : (
            <div className="w-full aspect-video bg-[#050003] border border-[#2A0F3D] flex items-center justify-center mb-3">
              <span className="text-[#4B6B52] font-mono text-xs uppercase">// no image</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-white text-xl sm:text-2xl font-bold uppercase leading-tight">
              {product.name}
            </h1>
            {onToggleFavorite && (
              <button
                onClick={() => onToggleFavorite(product.id)}
                className={`p-2 border ${isFavorite ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-[#2A0F3D] text-[#4B6B52] hover:text-[#FF3B30] hover:border-[#FF3B30]'} transition-colors flex-shrink-0`}
                data-testid={`drawer-fav-${product.id}`}
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-[#FF3B30]' : ''}`} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 mb-3 flex-wrap">
            {product.strain_type && (
              <span className="text-[10px] font-mono uppercase border border-[#39FF14] text-[#39FF14] px-1.5 py-0.5">
                {product.strain_type}
              </span>
            )}
            <span className="text-[#39FF14] font-mono font-bold">
              from ${Number(fromPrice).toFixed(0)}
            </span>
            {isOutOfStock && (
              <span className="text-[10px] font-mono uppercase border border-[#FF3B30] text-[#FF3B30] px-1.5 py-0.5">
                SOLD OUT
              </span>
            )}
          </div>
          {product.vibe_description && (
            <p className="text-[#A3C7A8] text-xs italic font-mono mb-2">
              {'//'} {product.vibe_description}
            </p>
          )}
          {product.description && (
            <p className="text-[#A3C7A8] text-sm leading-relaxed">{product.description}</p>
          )}

          {/* Effect chips (flower only) */}
          {effects.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`drawer-effects-${product.id}`}>
              {effects.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[#2A0F3D] text-[#A3C7A8] font-mono text-[10px] uppercase tracking-wider"
                >
                  <span aria-hidden="true">{effectEmoji(e)}</span>
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>

        {isOutOfStock && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
            <SoldOutButton product={product} testId="drawer-sold-out-btn" />
          </div>
        )}

        {/* Size / variant selector */}
        {!isOutOfStock && isFlower && (
          <div ref={variantsBlockRef} className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="drawer-flower-sizes">
            <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm mb-3">
              {'>'} SELECT SIZE
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FLOWER_SIZES.map((s) => {
                const perProduct = flowerSizes.find((x) => x.key === s.key);
                const price = perProduct ? perProduct.price : s.price;
                const active = s.key === selectedSize;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSelectedSize(s.key)}
                    data-testid={`drawer-size-${s.key}`}
                    className={`p-3 border rounded-none text-center transition-colors ${
                      active
                        ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)]'
                        : 'border-[#2A0F3D] hover:border-[#A3C7A8]'
                    }`}
                  >
                    <div className={`font-mono font-bold text-base ${active ? 'text-[#39FF14]' : 'text-white'}`}>
                      {s.label}
                    </div>
                    <div className={`font-mono font-bold text-lg mt-1 ${active ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>
                      ${price}
                    </div>
                  </button>
                );
              })}
            </div>
            <InlineAddToCart
              displayPrice={displayPrice}
              disabled={addDisabled}
              onClick={handleAddToCart}
            />
          </div>
        )}

        {!isOutOfStock && isVariantMode && (
          <div ref={variantsBlockRef} className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="drawer-variants">
            <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm mb-3">
              {'>'} SELECT {product.category === 'concentrates' ? 'WEIGHT' : 'SIZE'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {variants.map((v) => {
                const vStock = variantStockFor(product, v.key, region);
                const oos = vStock <= 0;
                const vPrice = variantPriceFor(product, v.key, region);
                const active = v.key === selectedSize;
                return (
                  <button
                    key={v.key}
                    type="button"
                    disabled={oos}
                    onClick={() => setSelectedSize(v.key)}
                    data-testid={`drawer-variant-${v.key}`}
                    className={`p-3 border rounded-none text-center transition-colors ${
                      oos
                        ? 'border-[#2A0F3D] text-[#4B6B52] line-through cursor-not-allowed opacity-50'
                        : active
                          ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)]'
                          : 'border-[#2A0F3D] hover:border-[#A3C7A8]'
                    }`}
                    title={oos ? 'Out of stock in your area' : `${v.label} · $${vPrice}`}
                  >
                    <div className={`font-mono font-bold text-base ${active ? 'text-[#39FF14]' : 'text-white'}`}>
                      {v.label}
                    </div>
                    {v.price ? (
                      <div className={`font-mono font-bold text-lg mt-1 ${active ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>
                        ${vPrice}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <InlineAddToCart
              displayPrice={displayPrice}
              disabled={addDisabled}
              onClick={handleAddToCart}
            />
          </div>
        )}

        {/* Simple product (no variants, no flower) — qty + add */}
        {!isOutOfStock && !isFlower && !isVariantMode && (
          <div ref={variantsBlockRef} className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="drawer-simple-buy">
            <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm mb-3">
              {'>'} QUANTITY
            </h3>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center border border-[#2A0F3D]">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                  data-testid="drawer-decrease-qty"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 text-[#39FF14] font-mono">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(customerEffectiveStock(product, region) || 1, quantity + 1))}
                  className="p-2 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                  data-testid="drawer-increase-qty"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <span className="text-[#39FF14] font-mono font-bold text-lg">
                ${Number(displayPrice * quantity).toFixed(2)}
              </span>
            </div>
            <InlineAddToCart
              displayPrice={displayPrice * quantity}
              disabled={addDisabled}
              onClick={handleAddToCart}
            />
          </div>
        )}

        {/* Cannabinoids panel */}
        {cb && Object.keys(cb).length > 0 && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="drawer-cannabinoids">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm">
                {'>'} CANNABINOIDS
              </h3>
              <span className="text-[10px] text-[#6B8E73] font-mono">{'//'} from COA</span>
            </div>
            <p className="text-[#A3C7A8] text-xs leading-relaxed mb-3">
              Chemical compounds in cannabis that interact with the body&apos;s endocannabinoid system.
            </p>

            {cb.THCA != null && (
              <div className="mb-3">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-white font-mono font-bold text-sm">THCA</span>
                  <span className="text-[#39FF14] font-mono font-bold">{cb.THCA}%</span>
                </div>
                <div className="h-2 bg-black border border-[#2A0F3D]">
                  <div
                    className="h-full bg-[#39FF14]"
                    style={{ width: `${Math.min(Number(cb.THCA) * 2, 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs font-mono">
              {Object.entries(cb)
                .filter(([k]) => k !== 'THCA')
                .map(([k, v]) => {
                  const fact = cannabinoidFact(k);
                  const isOpen = expandedCb === k;
                  const clickable = !!fact;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => clickable && setExpandedCb(isOpen ? null : k)}
                      disabled={!clickable}
                      className={`flex items-center justify-between bg-black/30 border px-2 py-1.5 text-left transition-colors ${
                        isOpen
                          ? 'border-[#39FF14] bg-[rgba(57,255,20,0.06)]'
                          : 'border-[#2A0F3D] hover:border-[#39FF14]/50'
                      } ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                      data-testid={`drawer-cb-${k}`}
                      aria-expanded={isOpen}
                      aria-label={fact ? `${k} — tap to learn` : k}
                    >
                      <span className="text-[#A3C7A8] uppercase">{k}</span>
                      <span className="text-[#39FF14]">{v}%</span>
                      <Info className={`w-3 h-3 ml-1 ${clickable ? 'text-[#39FF14]/70' : 'text-[#6B8E73]'}`} />
                    </button>
                  );
                })}
            </div>
            {expandedCb && cannabinoidFact(expandedCb) && (
              <div
                className="mt-3 bg-black/40 border border-[#39FF14]/40 px-3 py-2 text-xs font-mono leading-relaxed"
                data-testid={`drawer-cb-fact-${expandedCb}`}
              >
                <span className="text-[#39FF14] font-bold uppercase mr-1">{expandedCb}</span>
                <span className="text-[#A3C7A8]">{cannabinoidFact(expandedCb)}</span>
              </div>
            )}
          </div>
        )}

        {/* Terpenes panel */}
        {tp && Object.keys(tp).length > 0 && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="drawer-terpenes">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#39FF14] font-mono uppercase tracking-wider text-sm">
                {'>'} TERPENES
              </h3>
              <span className="text-[10px] text-[#6B8E73] font-mono">{'//'} flavor & effect</span>
            </div>
            <p className="text-[#A3C7A8] text-xs leading-relaxed mb-3">
              Terpenes shape the smell and taste of cannabis. They team up with cannabinoids to produce the variety of effects cannabis is known for.
            </p>

            {tp.total != null && (
              <div className="mb-3 bg-black/30 border border-[#39FF14]/40 p-2 flex items-center justify-between">
                <span className="text-white font-mono font-bold text-sm">Total Terpenes</span>
                <span className="text-[#39FF14] font-mono font-bold text-lg">{tp.total}%</span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(tp)
                .filter(([k]) => k !== 'total')
                .map(([k, v]) => {
                  const fact = terpeneFact(k);
                  const isOpen = expandedTp === k;
                  const clickable = !!fact;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => clickable && setExpandedTp(isOpen ? null : k)}
                      disabled={!clickable}
                      className={`flex items-center gap-2 bg-black/30 border px-2 py-2 text-left transition-colors ${
                        isOpen
                          ? 'border-[#39FF14] bg-[rgba(57,255,20,0.06)]'
                          : 'border-[#2A0F3D] hover:border-[#39FF14]/50'
                      } ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                      data-testid={`drawer-tp-${k}`}
                      aria-expanded={isOpen}
                      aria-label={fact ? `${k} — tap to learn` : k}
                    >
                      <Leaf className={`w-4 h-4 flex-shrink-0 ${isOpen ? 'text-[#39FF14]' : 'text-[#39FF14]/80'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[#A3C7A8] text-[10px] font-mono leading-tight truncate">{k}</p>
                        <p className="text-[#39FF14] text-xs font-mono font-bold">{v}%</p>
                      </div>
                    </button>
                  );
                })}
            </div>
            {expandedTp && terpeneFact(expandedTp) && (
              <div
                className="mt-3 bg-black/40 border border-[#39FF14]/40 px-3 py-2 text-xs font-mono leading-relaxed"
                data-testid={`drawer-tp-fact-${expandedTp}`}
              >
                <span className="text-[#39FF14] font-bold uppercase mr-1">{expandedTp}</span>
                <span className="text-[#A3C7A8]">{terpeneFact(expandedTp)}</span>
              </div>
            )}
          </div>
        )}

        {/* Feb 2026 — Reviews section (Track B). Gated on `customer.reviews`
            so the drawer stays clean until launch comms go out. Renders
            three things when the flag flips ON:
              1. Header + "Post a review" CTA button (opens ReviewSubmitModal)
              2. ProductReviewsSection — approved review cards with fire
                 like button + author + media
              3. Extra bottom padding so the sticky ADD-TO-CART overlay
                 doesn't cover the last review card
            When the flag is OFF the whole block is null — zero DOM cost,
            no accidental network calls, aggregate rating badge above the
            price still renders (rating reads aren't gated). */}
        {reviewsEnabled && product?.id && (
          <div
            className="mt-6 pt-4 border-t border-[#2A0F3D]"
            data-testid="drawer-reviews-section"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[#39FF14] font-mono text-sm font-bold uppercase tracking-wider">
                {'>'} Reviews
              </p>
              <ReviewWindowButton productId={product.id} onOpen={() => setShowReviewModal(true)} />
            </div>
            <ProductReviewsSection productId={product.id} />
            {/* Sticky CTA is 60-ish px tall — leave breathing room. */}
            <div className="h-20" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Sticky CTA — only appears after the inline CTA scrolls out of view */}
      {!isOutOfStock && showStickyCta && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-[#050003] border-t border-[#2A0F3D] p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.6)]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          data-testid="drawer-sticky-cta"
        >
          <div className="max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={addDisabled}
              className="w-full bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] disabled:bg-[#2A0F3D] disabled:text-[#4B6B52] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none flex items-center justify-center gap-2"
              data-testid="drawer-sticky-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4" />
              ADD TO CART · ${Number(displayPrice).toFixed(0)}
            </button>
          </div>
        </div>
      )}

      {/* Feb 2026 — Review submit modal. Rendered as a sibling of the
          sticky CTA so its own fixed overlay z-index sits above the CTA. */}
      {reviewsEnabled && showReviewModal && (
        <ReviewSubmitModal
          productId={product.id}
          productName={product.name}
          onClose={() => setShowReviewModal(false)}
          onSubmitted={() => setShowReviewModal(false)}
        />
      )}
    </div>
  );
}

function InlineAddToCart({ displayPrice, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full mt-3 bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] disabled:bg-[#2A0F3D] disabled:text-[#4B6B52] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none flex items-center justify-center gap-2"
      data-testid="drawer-inline-add-to-cart"
    >
      <ShoppingCart className="w-4 h-4" />
      ADD TO CART · ${Number(displayPrice).toFixed(0)}
    </button>
  );
}

/**
 * FeaturedCard — hero card rendered above the grid for products flagged
 * `featured: true`. Mirrors the prototype layout. Tapping it opens the drawer.
 *
 * `fromPrice` is the lowest-tier price for the active region (computed by
 * the parent via `computeFromPrice`). For flower products, `product.price`
 * is 0 by design (the ladder is the source of truth), so passing this prop
 * is REQUIRED to render a non-zero price. Without it, flower featured cards
 * display $0 — see Feb 2026 inventory price bug.
 */
export function FeaturedCard({ product, fromPrice, onOpen }) {
  const displayPrice = Number(fromPrice ?? product.price ?? 0);
  const isFrom = product?.category === 'flower' || (fromPrice != null && fromPrice !== product.price);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left bg-gradient-to-br from-[#0C1810] to-[#0C0612] border border-[#2A0F3D] hover:border-[#39FF14] transition-colors rounded-none p-3 sm:p-4 flex gap-3 sm:gap-4 group"
      data-testid={`featured-card-${product.id}`}
    >
      <div className="w-28 sm:w-40 flex-shrink-0">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full aspect-square object-cover rounded-sm"
          />
        ) : (
          <div className="w-full aspect-square bg-[#050003] border border-[#2A0F3D]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-white font-bold text-lg sm:text-xl uppercase tracking-wide leading-tight truncate">
            {product.name}
          </h3>
          <span
            className="text-[#39FF14] font-mono font-bold whitespace-nowrap"
            data-testid={`featured-card-price-${product.id}`}
          >
            {isFrom ? <span className="text-xs opacity-80 mr-0.5">from</span> : null}${Number(displayPrice ?? 0).toFixed(0)}
          </span>
        </div>
        {/* Phase 6.0 — THCA % moved into the detail drawer's CANNABINOIDS
            panel (still tap-to-learn). Keeping the dashboard card minimal:
            strain pill only. */}
        {product.strain_type && (
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider border border-[#2A0F3D] text-[#A3C7A8] px-1.5 py-0.5 rounded-none">
              {product.strain_type}
            </span>
          </div>
        )}
        <div className="mt-2">
          <ProductRatingBadge
            stats={product.review_stats}
            theme="dark"
            testId={`featured-card-rating-${product.id}`}
          />
        </div>
        <p className="text-[#A3C7A8] text-sm mt-2 line-clamp-3 leading-relaxed">
          {product.vibe_description || product.description}
        </p>
        <div className="text-[#39FF14] text-xs font-mono mt-2 opacity-70 group-hover:opacity-100 flex items-center gap-1">
          View details <ChevronDown className="w-3 h-3" />
        </div>
      </div>
    </button>
  );
}


/**
 * MiniGridCard — compact thumbnail card for the FULL MENU_ section.
 * Matches the approved prototype layout (CustomerDashboardPreview.js GridCard):
 * square image + name + one-line tagline + price. Tap → opens detail drawer.
 *
 * Backward compatible — falls back gracefully when image_url, vibe_description,
 * or per-variant pricing are missing. `fromPrice` is the lowest-tier price
 * the dashboard already computed (flowerSizes / variants).
 */
export function MiniGridCard({ product, fromPrice, onOpen }) {
  const tagline = product.vibe_description || product.description || '';
  const price = Number(fromPrice ?? product.price ?? 0);
  const soldOut = !!product.sold_out;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(); } }}
      className="bg-[#0C0612] border border-[#2A0F3D] hover:border-[#39FF14] transition-colors rounded-none overflow-hidden text-left cursor-pointer focus:outline-none focus:border-[#39FF14]"
      data-testid={`mini-grid-card-${product.id}`}
      aria-label={`View ${product.name} details`}
    >
      <div className="relative">
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.name}
          className={`w-full aspect-square object-cover ${soldOut ? 'opacity-40' : ''}`}
          loading="lazy"
        />
      ) : (
        <div className="w-full aspect-square bg-[#050003] border-b border-[#2A0F3D] flex items-center justify-center">
          {!soldOut && <span className="text-[#4B6B52] font-mono text-[10px] uppercase">// no image</span>}
        </div>
      )}
      {soldOut && (
        <span className="absolute inset-0 flex items-center justify-center text-[#FF3B30] font-mono text-xs font-bold uppercase" data-testid={`sold-out-label-${product.id}`}>
          SOLD OUT
        </span>
      )}
      </div>
      <div className="p-2">
        <p className="text-[#39FF14] font-mono text-xs font-bold truncate">{product.name}</p>
        <p className="text-[#6B8E73] text-[10px] font-mono truncate">{tagline}</p>
        <div className="flex items-center justify-between mt-1 gap-2">
          <p className="text-[#39FF14] text-sm font-bold">${Number(price ?? 0).toFixed(0)}</p>
          <ProductRatingBadge
            stats={product.review_stats}
            theme="dark"
            testId={`mini-grid-rating-${product.id}`}
          />
        </div>
        {soldOut && <div className="mt-2"><SoldOutButton product={product} testId={`sold-out-btn-${product.id}`} /></div>}
      </div>
    </div>
  );
}
